<script setup lang="ts">
/**
 * MY — Allotment of Shares (Bonus Issue, M32203) 문서-뷰 패널.
 *
 * OTC(PreSetAllotmentOtcPanel) 미러링. "한 task = 8문서 패키지 + 공유 values 맵".
 * 오른쪽 = 4단계 문서 패키지, 왼쪽 = 챗봇(person lookup). 변수 클릭 → 챗봇/상단 입력바/주소 입력으로 채운다.
 * (Bonus 는 준비금 자본전입 무상배정 — 청약자 유형 분기 없음.)
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  BONUS_DOCS, BONUS_DOC_CODES, labelize, typeOf, comboSourceOf, isOptional,
  parseClauseNo, clauseBasisOf,
  BONUS_ALLOTTEE_MAX, BONUS_SHARE_CLASS_MAX, type BonusDocCode,
} from './bonus/bonusFields';
import { BONUS_DOC_CTX } from './bonus/bonusContext';
import { buildBonusVariables, buildAllottees, emptyBonusState, type BonusState } from '../mappers/bonusIssueMapper';
import { comboOptions, resolveNationality, resolveOccupation, type ComboOpt } from './bonus/bonusOptions';
import { parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { formatMalaysiaIdForCdd } from './cdd/cddIdFormat';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import BonusIssuePackageDoc from './bonus/BonusIssuePackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  appliedClause?: { clauseNumber: string; governingBasis: 'constitution' | 'articles' | 'third_schedule' | null; rawOption?: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

// ─── Task Title ───────────────────────────────────────────────
const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

// ─── 공유 상태(BonusState) ──────────────────────────────────────
const state = reactive<BonusState>(emptyBonusState());
const allotteeCountRef = computed(() => state.allotteeCount);
const shareClassCountRef = computed(() => state.shareClassCount);

// ─── 활성 필드 + 입력바 ─────────────────────────────────────────
const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function activate(key: string) {
  activeKey.value = key;
  if (key === 'constitution_clause_number') {
    if (clauseLocked.value) { activeKey.value = ''; return; }
    emit('request-person-lookup', {
      fieldGroup: 'constitution-clause',
      key: 'constitution_clause_number',
      clauseOptions: clauseOptions.value,
      defaultValue: state.values.constitution_clause_number || '',
      defaultBasis: state.choices.resolution_basis || 'constitution',
    });
    inputKey.value = '';
    return;
  }
  const t = typeOf(key);
  // Nature of Business → MSIC 챗봇 검색
  if (t === 'msic' || key === 'bo_nature') {
    emit('request-person-lookup', {
      fieldGroup: 'bonus',
      key: 'bo_nature',
      lookupType: 'msic',
    });
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    // 비서/이사/BO 필드는 해당 역할 목록으로 바로 진입, 나머지는 역할 선택 버튼부터.
    const defaultRole = /^(cosec_name|secretary_name)$/.test(key) ? 'SECRETARY'
      : /^director_name$/.test(key) ? 'DIRECTOR'
      : /^bo_full_name$/.test(key) ? 'BENEFICIAL_OWNER'
      : null;
    emit('request-person-lookup', { fieldGroup: 'bonus', key, defaultRole });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

const numOnly = (v: any) => Number(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;

/** shares ↔ otherwise, amount ↔ capitalized 페어 미러.
 *  shares 변경 시 Amount Capitalized = shares × share_price 자동 계산.
 *  share_class number/price 변경 시 Total Issued = number × price 자동 계산.
 *  Clear(빈값)면 페어·금액도 함께 비움. */
function setFieldValue(key: string, val: string) {
  state.values[key] = val;

  const allotteeM = key.match(/^allottee_(shares_otherwise|shares|amount_capitalized|amount)_(\d+)$/);
  if (allotteeM) {
    const kind = allotteeM[1];
    const n = allotteeM[2];
    if (kind === 'shares' || kind === 'shares_otherwise') {
      state.values[`allottee_shares_${n}`] = val;
      state.values[`allottee_shares_otherwise_${n}`] = val;
      const sh = numOnly(val);
      const price = numOnly(state.values.share_price);
      const amount = sh && price ? String(sh * price) : '';
      state.values[`allottee_amount_${n}`] = amount;
      state.values[`allottee_amount_capitalized_${n}`] = amount;
    } else {
      state.values[`allottee_amount_${n}`] = val;
      state.values[`allottee_amount_capitalized_${n}`] = val;
    }
    return;
  }

  const shareClassM = key.match(/^share_class_(number|price|total)_(\d+)$/);
  if (shareClassM) {
    const kind = shareClassM[1];
    const n = shareClassM[2];
    if (kind === 'number' || kind === 'price') {
      const number = numOnly(kind === 'number' ? val : state.values[`share_class_number_${n}`]);
      const price = numOnly(kind === 'price' ? val : state.values[`share_class_price_${n}`]);
      state.values[`share_class_total_${n}`] = number && price ? String(number * price) : '';
    }
  }
}

// ── 조건 토글 ──
function flag(name: string): boolean { return !!state.flags[name]; }
function setFlag(name: string, val: boolean) { state.flags[name] = val; }
function toggleFlag(name: string) { state.flags[name] = !state.flags[name]; }
function choiceOf(name: string): string { return state.choices[name] || ''; }
function setChoice(name: string, val: string) {
  if (name === 'resolution_basis' && clauseLocked.value) return;
  state.choices[name] = val;
}

// ── Governing Document (정관 결의 근거) ──
const clauseLocked = ref(false);
const clauseOptions = ref<string[]>([]);
function setClause(raw: string) {
  if (clauseLocked.value) return;
  const s = String(raw || '');
  const basis = clauseBasisOf(s);
  if (basis !== 'other') state.choices.resolution_basis = basis;
  state.values.constitution_clause_number = basis === 'third_schedule' ? '' : parseClauseNo(s);
}

// ── 반복부: Allottees ──
function syncAllotteeNamesSummary() {
  const names: string[] = [];
  for (let n = 1; n <= state.allotteeCount; n++) {
    const name = String(state.values[`allottee_name_${n}`] || '').trim();
    if (name) names.push(name);
  }
  state.values.allottee_names_summary = names.join(', ');
}

function addAllottee() {
  if (state.allotteeCount < BONUS_ALLOTTEE_MAX) state.allotteeCount += 1;
  syncAllotteeNamesSummary();
}
const ALLOTTEE_FIELDS = [
  'name', 'address', 'shares', 'shares_otherwise', 'id_no', 'amount', 'amount_capitalized', 'share_type',
];
function removeAllottee(index: number) {
  const total = state.allotteeCount;
  if (total <= 1) {
    ALLOTTEE_FIELDS.forEach((f) => { state.values[`allottee_${f}_1`] = ''; });
    state.values.allottee_contact_cd_1 = '';
    syncAllotteeNamesSummary();
    return;
  }
  for (let i = index; i < total; i++) {
    ALLOTTEE_FIELDS.forEach((f) => {
      state.values[`allottee_${f}_${i}`] = state.values[`allottee_${f}_${i + 1}`] || '';
    });
    state.values[`allottee_contact_cd_${i}`] = state.values[`allottee_contact_cd_${i + 1}`] || '';
  }
  ALLOTTEE_FIELDS.forEach((f) => { state.values[`allottee_${f}_${total}`] = ''; });
  state.values[`allottee_contact_cd_${total}`] = '';
  state.allotteeCount = total - 1;
  syncAllotteeNamesSummary();
}

/** 표의 allottee_name_* 변경 시 문서 본문 이름 요약(파란 박스) 자동 갱신 */
watch(
  () => {
    const names: string[] = [];
    for (let n = 1; n <= state.allotteeCount; n++) {
      names.push(String(state.values[`allottee_name_${n}`] || ''));
    }
    return `${state.allotteeCount}|${names.join('\u0001')}`;
  },
  () => { syncAllotteeNamesSummary(); },
  { immediate: true },
);

// ── 반복부: Share classes (Section 78) ──
function addShareClass() { if (state.shareClassCount < BONUS_SHARE_CLASS_MAX) state.shareClassCount += 1; }
const SHARE_FIELDS = ['type', 'details', 'number', 'price', 'total'];
function removeShareClass(index: number) {
  const total = state.shareClassCount;
  if (total <= 1) {
    SHARE_FIELDS.forEach((f) => { state.values[`share_class_${f}_1`] = ''; });
    return;
  }
  for (let i = index; i < total; i++) {
    SHARE_FIELDS.forEach((f) => {
      state.values[`share_class_${f}_${i}`] = state.values[`share_class_${f}_${i + 1}`] || '';
    });
  }
  SHARE_FIELDS.forEach((f) => { state.values[`share_class_${f}_${total}`] = ''; });
  state.shareClassCount = total - 1;
}

// ── [[Signers]] — 문서별 서명자 선택 ──
function signersOf(varKey: string): any[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: any[]) { state.signers[varKey] = Array.isArray(list) ? list : []; }

function comboOptionsOf(key: string): ComboOpt[] { return comboOptions(comboSourceOf(key)); }

// ── Total New Shares 자동 합산 (각 allottee 행 주식수의 합) ──
const allotteeSharesTotal = computed(() => {
  let sum = 0;
  for (let n = 1; n <= state.allotteeCount; n++) {
    const raw = state.values[`allottee_shares_${n}`] || state.values[`allottee_shares_otherwise_${n}`];
    sum += Number(String(raw ?? '').replace(/[^0-9.]/g, '')) || 0;
  }
  return sum;
});
watch(allotteeSharesTotal, (v) => {
  state.values.total_new_shares = v ? String(v) : '';
}, { immediate: true });

// 단가(share_price) 변경 시 기존 행 Amount Capitalized 재계산
watch(() => state.values.share_price, () => {
  const price = numOnly(state.values.share_price);
  for (let n = 1; n <= state.allotteeCount; n++) {
    const sh = numOnly(state.values[`allottee_shares_${n}`] || state.values[`allottee_shares_otherwise_${n}`]);
    if (!sh) continue;
    const amount = price ? String(sh * price) : '';
    state.values[`allottee_amount_${n}`] = amount;
    state.values[`allottee_amount_capitalized_${n}`] = amount;
  }
});

// BON02 템플릿 [[shares_offered]] / [[shares_accepted]] — shares_subscribed 와 동기화(비어 있을 때만).
watch(() => state.values.shares_subscribed, (v) => {
  const s = String(v ?? '').trim();
  if (!s) return;
  if (!String(state.values.shares_offered || '').trim()) state.values.shares_offered = s;
  if (!String(state.values.shares_accepted || '').trim()) state.values.shares_accepted = s;
});

// ── S78 B. TOTALS OF SHARES ALLOTTED — Total (RM) = Cash + Otherwise 자동 합산 ──
const numOf = (v: any) => Number(String(v ?? '').replace(/[^0-9.]/g, '')) || 0;
const s78Totals = computed(() => ({
  total_current: numOf(state.values.total_current_cash) + numOf(state.values.total_current_otherwise),
  total_additional: numOf(state.values.total_additional_cash) + numOf(state.values.total_additional_otherwise),
  total_accumulated: numOf(state.values.total_accumulated_cash) + numOf(state.values.total_accumulated_otherwise),
}));
watch(s78Totals, (t) => {
  state.values.total_current = t.total_current ? String(t.total_current) : '';
  state.values.total_additional = t.total_additional ? String(t.total_additional) : '';
  state.values.total_accumulated = t.total_accumulated ? String(t.total_accumulated) : '';
}, { immediate: true, deep: true });

// ─── 레지스터 write 용 shareholders 배열 (bonus 는 무상배정 → sharesPayable=Otherwise/B06002) ───
function buildShareholdersForRegister(): any[] {
  const price = numOnly(state.values.share_price);
  const rows: any[] = [];
  for (let n = 1; n <= state.allotteeCount; n++) {
    const contactCd = String(state.values[`allottee_contact_cd_${n}`] || '').trim();
    const name = String(state.values[`allottee_name_${n}`] || '').trim();
    const shares = numOnly(state.values[`allottee_shares_${n}`])
      || numOnly(state.values[`allottee_shares_otherwise_${n}`]);
    if (!contactCd || shares <= 0) continue;
    const amount = price > 0 ? Math.round(shares * price * 100) / 100 : 0;
    const shareClass = String(state.values[`allottee_share_type_${n}`] || '').trim() || 'Ordinary';
    rows.push({
      shareholder: contactCd,
      shareholderName: name,
      currencies: [{
        currency: 'B08114',
        shareClasses: [{
          classOfShares: shareClass,
          classOfSharesName: shareClass,
          numberOfShares: shares,
          issuedShareCapital: amount,
          paidUpShareCapital: amount,
          // Bonus Issue = Otherwise Than Cash (Register MY allot 과 동일 경로, event_type=Allot)
          sharesPayable: 'B06002',
        }],
      }],
    });
  }
  return rows;
}

/** 주소 정규화: 줄바꿈/중복 콤마 → 한 줄(콤마 구분). */
function flatAddr(v: any): string {
  return String(v ?? '').split(/[\n,]+/).map((x) => x.trim()).filter(Boolean).join(', ');
}

// ── pick-place ──
const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

// ─── 주소 입력 ─────────────────────────────────────────────────
const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
    .map((s) => String(s || '').trim()).filter(Boolean).join(', ');
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

watch(() => props.appliedClause, (a) => {
  if (!a || clauseLocked.value) return;
  const detected = clauseBasisOf(a.rawOption || '');
  const basis = a.governingBasis || (detected !== 'other' ? detected : null) || 'constitution';
  state.choices.resolution_basis = basis;
  state.values.constitution_clause_number = basis === 'third_schedule' ? '' : (a.clauseNumber || '');
});

// 챗봇 MSIC 선택 → Nature of Business(bo_nature) 반영
watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    const nature = fd.bo_nature;
    if (nature != null && String(nature).trim()) {
      state.values.bo_nature = String(nature).trim();
    }
  },
  { deep: true },
);

// ─── 사람 상세 → 필드 채움 (챗봇 선택) ───────────────────────────
function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;

  const id = formatMalaysiaIdForCdd(a.nricNo || a.passportNo || a.contactRegNo || '');
  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);
  const addrLine = !isEmptyMyAddress(res)
    ? [res.street, [res.postcode, res.town].filter(Boolean).join(' '), res.state, res.country].map((s) => String(s || '').trim()).filter(Boolean).join(', ')
    : fullAddr;
  const nat = resolveNationality(String(a.nationalityName || '')) || resolveNationality(String(a.nationality || ''));
  const occ = resolveOccupation(a.occupation || a.businessOccupation || '');
  const phone = [a.areaCode, a.mobileNo].filter(Boolean).join(' ').trim() || a.mobileNo || '';
  const dob = (a.dateOfBirth || '').toString().split(/[ T]/)[0];

  const m = key.match(/^(.*?)(?:_full_name|_name)(_\d+)?$/);
  const prefix = m ? m[1] : '';
  const idx = m && m[2] ? m[2] : '';
  const put = (k: string, val: string) => { if (val) state.values[k] = val; };

  if (prefix === 'subscriber') {
    put('subscriber_id', id); put('subscriber_address', addrLine);
    // 개인/법인 판별 → Person / Company. 국적/생년월일은 개인일 때만.
    const catCd = String(a.categoryCd || a.CATEGORY_CD || a.contactCategoryCd || '').toUpperCase();
    const typeStr = String(a.contactType || a.categoryName || a.entityType || '').toLowerCase();
    const looksCompany = catCd === 'B09001'
      || /corporat|company|legal\s*person|body\s*corporate|\b(pte|ltd|sdn|bhd|inc|llp)\b/.test(typeStr)
      || (!!a.contactRegNo && !a.nricNo && !a.passportNo && !a.dateOfBirth);
    const looksPerson = catCd === 'B09002' || !!a.nricNo || !!a.passportNo || !!a.dateOfBirth || /individual|person/.test(typeStr);
    const isPerson = looksPerson || !looksCompany;
    state.values.subscriber_type = isPerson ? 'Person' : 'Company';
    if (isPerson) { put('subscriber_dob', dob); put('subscriber_nationality', nat); }
  } else if (prefix === 'allottee') {
    put(`allottee_id_no${idx}`, id); put(`allottee_address${idx}`, addrLine);
    const cc = a.contactCd || a.contactCD || a.CONTACT_CD || '';
    if (cc) state.values[`allottee_contact_cd${idx}`] = String(cc);
  } else if (prefix === 'director') {
    put('director_nric', id);
  } else if (prefix === 'lodger') {
    put('lodger_nric', id); put('lodger_address', addrLine); put('lodger_phone', phone); put('lodger_email', a.email || a.emailAddress || '');
  } else if (prefix === 'secretary' || prefix === 'cosec') {
    put(`${prefix}_address`, addrLine);
    const sspc = a.ssmPractisingCertificateNumber || a.SecretaryCertificateNo
      || a.SECRETARY_CERTIFICATE_NO || a.secretaryCertificateNo || '';
    put('secretary_sspc_no', String(sspc || ''));
  } else if (prefix === 'bo' || prefix === 'bo_full' || key === 'bo_full_name') {
    // Beneficial Owner — Full Name 선택 시 형제 필드 자동 채움(값 있을 때만)
    //   ※ bo_full_name 정규식은 (_full_name 우선) prefix='bo' 로 매칭됨
    //   Nature of Business(bo_nature)는 MSIC 챗봇 조회 전용 → 여기서 채우지 않음
    const place = nat
      || resolveNationality(String(a.countryName || a.country || a.placeOfIncorporation || ''))
      || String(a.nationalityName || a.countryName || a.country || '').trim();
    put('bo_place', place);

    // L_GLOBAL_CONTACT.CONTACT_REG_NO 우선 (list/enrich 모두 contactRegNo 로 정규화됨)
    const regNo = String(
      a.contactRegNo || a.CONTACT_REG_NO || a.regNo || a.registrationNo
      || a.nricNo || a.passportNo || id || '',
    ).trim();
    put('bo_registration_no', regNo ? formatMalaysiaIdForCdd(regNo) || regNo : '');

    const addrs = Array.isArray(a.addresses) ? a.addresses : [];
    const addrFromArr = (x: any) => flatAddr(x?.composed || x?.value || x?.address || '');
    // type 컬럼이 'address'/'branch' 인 신주소 옵션은 originalLabel/label 에 Residential/Business 가 있음
    const addrKind = (x: any) => [
      x?.type, x?.addressType, x?.addressTypeName, x?.label, x?.originalLabel,
    ].map((s) => String(s || '')).join(' ');
    const isResidential = (x: any) => /residential|registered|home/i.test(addrKind(x));
    const isBusiness = (x: any) => /business|office|principal/i.test(addrKind(x))
      && !/residential|registered|home/i.test(addrKind(x));

    const registered = flatAddr(addrLine)
      || flatAddr(a.primaryAddress || a.residentialAddress || '')
      || addrFromArr(addrs.find(isResidential))
      || addrFromArr(addrs[0]);
    put('bo_registered_address', registered);

    // Business 는 MAP ADDR_TYPE=M04003(또는 명시 business 라벨)이 있을 때만.
    //   Residential 1건만 있으면 Registered 만 채움 — registered 로 fallback 금지.
    const bizAddr = addrFromArr(addrs.find(isBusiness))
      || flatAddr(typeof a.businessAddress === 'string' ? a.businessAddress : '')
      || flatAddr(a.officeAddress || '');
    put('bo_business_address', bizAddr);
  }
  void occ;
}
watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'bonus' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(BONUS_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize, typeOf, activeKey, activate, setFieldValue, openAddress,
  comboOptionsOf,
  flag, setFlag, toggleFlag, choiceOf, setChoice,
  clauseLocked, clauseOptions, setClause,
  allotteeCount: allotteeCountRef, addAllottee, removeAllottee,
  shareClassCount: shareClassCountRef, addShareClass, removeShareClass,
  entityCd, signersOf, setSigners,
  pendingPickValue, placePick,
});

// ─── 자동 서명자([[Signers]]) 채움 헬퍼 ─────────────────────────
//   문서별 규칙(사용자 지정):
//     BON01 authority_dcr = 이사 전원 / authority_members = 사원(members) 전원
//     BON04 increase       = 이사 전원
//     BON07 statement      = 이사 전원
//     BON03 letter / BON05 s76_director / BON08 s78_return = 이사 1인
//     BON02 application     = 청약자(subscriber) 본인
//   (BON06 Section 76 Secretary 는 서명표에 라이선스 비서 1인이 자동 기입됨)
function dirToSigner(d: any): SignerEntry {
  const idx = d.officerIdx ?? d.OFFICER_IDX;
  const cc = String(d.contactCd || d.CONTACT_CD || '').trim();
  return {
    id: `dir-${idx ?? cc ?? d.name}`,
    name: d.name || d.contactName || d.CONTACT_NAME || '',
    title: 'Director', role: 'director', showTitle: true,
    officerIdx: typeof idx === 'number' ? idx : undefined,
    contactCd: cc || undefined,
    _signerLayout: 'box',
  };
}
function memberToSigner(m: any): SignerEntry {
  const cc = String(m.contactCd || m.CONTACT_CD || m.shareholderCd || '').trim();
  return {
    id: `mem-${cc || m.name}`,
    name: m.name || m.contactName || m.shareholderName || m.memberName || m.CONTACT_NAME || '',
    title: 'Member', role: 'shareholder', showTitle: true,
    contactCd: cc || undefined,
    _signerLayout: 'box',
  };
}
/** 동일 인물(contactCd 우선, 없으면 이름) 중복 제거 — members 는 주식클래스별로 여러 행이 올 수 있음. */
function dedupeSigners(list: SignerEntry[]): SignerEntry[] {
  const seen = new Set<string>();
  const out: SignerEntry[] = [];
  for (const s of list) {
    const key = (s.contactCd && `cc:${s.contactCd}`) || `nm:${String(s.name || '').trim().toLowerCase()}`;
    if (!key || key === 'nm:' || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}
/** 해당 서명영역이 아직 비어 있을 때만 자동 채움(사용자 수동 선택 보존). force 시 덮어씀(회사 변경). */
function autoSetSigners(varKey: string, list: SignerEntry[], force = false) {
  if (!list.length) return;
  if (!force && (state.signers[varKey] || []).length) return;
  state.signers[varKey] = list;
}

// 청약자 이름 → BON02 application 서명자 자동 동기화 (사용자가 직접 바꾼 경우는 보존).
watch(() => state.values.subscriber_name, (nm) => {
  const name = String(nm || '').trim();
  if (!name) return;
  const cur = state.signers['application'] || [];
  if (cur.length && !(cur.length === 1 && cur[0].id === 'subscriber')) return;
  state.signers['application'] = [{
    id: 'subscriber', name, title: '', role: 'contact', isManual: true, showTitle: false, _signerLayout: 'box',
  }];
});

// ─── Entity 선택 → 회사 필드 + Company Secretary(cosec) 자동 채움 ───
//   최초 선택: 빈 필드만 채움. 회사 Change(entityCd 변경): 회사 바인딩 필드를 강제 갱신.
const entityPrefilled = ref('');
watch(entityCd, async (cd) => {
  if (!cd || cd === entityPrefilled.value) return;
  const isEntitySwitch = !!entityPrefilled.value && entityPrefilled.value !== cd;
  entityPrefilled.value = cd;
  const put = (k: string, v: any, force = false) => {
    const s = String(v ?? '').trim();
    if (!s) return;
    if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
  };
  const force = isEntitySwitch;

  put('company_name', props.entityData?.companyName || props.entityData?.entityName, force);
  put('company_registration_no', props.entityData?.registrationNo, force);
  try {
    const gov = await fetchEntityAddresses(cd);
    if (gov) {
      put('company_name', gov.companyName, force);
      put('company_registration_no', gov.regNo, force);
      put('old_registration_no', gov.regNoOld, force);
      put('company_address', flatAddr(gov.registered), force);
      const regClause = String(gov.clauseNo || gov.articleNo || '').trim();
      if (regClause) {
        const basis = clauseBasisOf(regClause);
        state.choices.resolution_basis = basis === 'other' ? 'constitution' : basis;
        state.values.constitution_clause_number = basis === 'third_schedule' ? '' : parseClauseNo(regClause);
        clauseLocked.value = true;
      } else if (force) {
        state.values.constitution_clause_number = '';
        clauseLocked.value = false;
      } else {
        clauseLocked.value = false;
      }
    }
  } catch (e) { console.warn('[Bonus] entity address fetch failed', e); }
  try {
    const r = await fetch('/api/entity_my/profile/resolution-options', { credentials: 'include' }).then((x) => x.json());
    const opts = r?.articleNoOptions || r?.data?.resolution?.articleNoOptions || r?.resolution?.articleNoOptions || [];
    clauseOptions.value = Array.isArray(opts)
      ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
      : [];
  } catch (e) { console.warn('[Bonus] resolution-options fetch failed', e); }
  // 이사 목록 → sole/multiple 자동 + 문서별 이사 서명자 자동 채움
  try {
    const dr: any = await docGenApi.getDirectors(cd);
    const directors: any[] = dr?.data?.directors || dr?.directors || [];
    const active = directors.filter((d) => !String(d.cessationDate || d.CESSATION_DATE || d.dateOfCessation || '').trim());
    const useList = active.length ? active : directors;
    if (useList.length > 0) state.flags.is_multiple_director_company = useList.length > 1;
    else if (force) state.flags.is_multiple_director_company = false;
    const dirSigners = dedupeSigners(useList.map(dirToSigner).filter((s) => s.name));
    autoSetSigners('authority_dcr', dirSigners, force);
    autoSetSigners('increase', dirSigners, force);
    autoSetSigners('statement', dirSigners, force);
    const firstDir = dirSigners.slice(0, 1);
    autoSetSigners('letter', firstDir, force);
    autoSetSigners('s76_director', firstDir, force);
    autoSetSigners('s78_return', firstDir, force);
    if (firstDir[0]) {
      put('director_name', firstDir[0].name, force);
      const firstRaw = useList[0] || {};
      put(
        'director_nric',
        formatMalaysiaIdForCdd(String(firstRaw.contactRegNo || firstRaw.CONTACT_REG_NO || firstRaw.nric || ''))
          || String(firstRaw.contactRegNo || firstRaw.CONTACT_REG_NO || firstRaw.nric || ''),
        force,
      );
    } else if (force) {
      state.values.director_name = '';
      state.values.director_nric = '';
    }
  } catch (e) { console.warn('[Bonus] getDirectors failed', e); }
  // 사원(members) → sole/multiple member + BON01 서명자 + Letter of Allotment allottee 표
  try {
    const mres: any = await docGenApi.getMembers(cd);
    const members: any[] = mres?.data?.members || mres?.members || [];
    const memSigners = dedupeSigners(members.map(memberToSigner).filter((s) => s.name));
    if (memSigners.length > 0) state.flags.is_multiple_member = memSigners.length > 1;
    else if (force) state.flags.is_multiple_member = false;
    autoSetSigners('authority_members', memSigners, force);

    const allotteeAlreadySet = (() => {
      for (let n = 1; n <= state.allotteeCount; n++) {
        if (String(state.values[`allottee_name_${n}`] || '').trim()) return true;
        if (String(state.values[`allottee_contact_cd_${n}`] || '').trim()) return true;
      }
      return false;
    })();
    if ((force || !allotteeAlreadySet) && members.length > 0) {
      if (force) {
        for (let n = 1; n <= BONUS_ALLOTTEE_MAX; n++) {
          ALLOTTEE_FIELDS.forEach((f) => { state.values[`allottee_${f}_${n}`] = ''; });
          state.values[`allottee_contact_cd_${n}`] = '';
        }
        state.values.allottee_names_summary = '';
      }
      const seen = new Set<string>();
      const dedup: any[] = [];
      for (const m of members) {
        const cc = String(m.contactCd || m.CONTACT_CD || '').trim();
        const nm = String(m.name || m.contactName || m.CONTACT_NAME || '').trim();
        const key = cc ? `cc:${cc}` : `nm:${nm.toLowerCase()}`;
        if (!key || key === 'nm:' || seen.has(key)) continue;
        seen.add(key);
        dedup.push(m);
      }
      const list = dedup.slice(0, BONUS_ALLOTTEE_MAX);
      state.allotteeCount = Math.max(1, list.length);
      list.forEach((m, i) => {
        const n = i + 1;
        const addrs = Array.isArray(m.addresses) ? m.addresses : [];
        const addr = flatAddr(
          m.primaryAddress
          || addrs[0]?.value
          || addrs[0]?.address
          || addrs[0]?.composed
          || '',
        );
        const name = String(m.name || m.contactName || m.CONTACT_NAME || '').trim();
        const regNo = String(m.contactRegNo || m.CONTACT_REG_NO || '').trim();
        const idNo = formatMalaysiaIdForCdd(regNo) || regNo;
        const cc = String(m.contactCd || m.CONTACT_CD || '').trim();
        state.values[`allottee_name_${n}`] = name;
        state.values[`allottee_address_${n}`] = addr;
        state.values[`allottee_id_no_${n}`] = idNo;
        state.values[`allottee_contact_cd_${n}`] = cc;
      });
      syncAllotteeNamesSummary();
    } else if (force && members.length === 0) {
      for (let n = 1; n <= BONUS_ALLOTTEE_MAX; n++) {
        ALLOTTEE_FIELDS.forEach((f) => { state.values[`allottee_${f}_${n}`] = ''; });
        state.values[`allottee_contact_cd_${n}`] = '';
      }
      state.allotteeCount = 1;
      syncAllotteeNamesSummary();
    }
  } catch (e) { console.warn('[Bonus] getMembers failed', e); }
  try {
    const secRes: any = await docGenApi.getSecretaries(cd);
    const secs = secRes?.data?.secretaries || secRes?.data || [];
    if (Array.isArray(secs) && secs.length > 0) {
      const s = secs[0];
      const secCc = String(s.contactCd || s.contactCode || s.CONTACT_CD || '').trim();
      let det: any = s;
      if (secCc) {
        try { const r: any = await docGenApi.getContactDetail(cd, secCc); det = r?.data || r || s; } catch { /* noop */ }
      }
      const name = det.name || det.contactName || s.name || '';
      const reg = det.contactRegNo || det.licenseNumber || s.contactRegNo || '';
      const addrs = Array.isArray(det.addresses) ? det.addresses : [];
      const addr = addrs[0]?.composed || addrs[0]?.value || det.primaryAddress || det.residentialAddress || '';
      const sspc = det.ssmPractisingCertificateNumber || det.SecretaryCertificateNo
        || det.SECRETARY_CERTIFICATE_NO || det.secretaryCertificateNo
        || s.ssmPractisingCertificateNumber || s.SecretaryCertificateNo || '';
      put('cosec_name', name, force); put('cosec_address', flatAddr(addr), force);
      put('secretary_name', name, force); put('secretary_license_no', det.licenseNumber || reg, force);
      put('secretary_sspc_no', String(sspc || ''), force);
    } else if (force) {
      state.values.cosec_name = '';
      state.values.cosec_address = '';
      state.values.secretary_name = '';
      state.values.secretary_license_no = '';
      state.values.secretary_sspc_no = '';
    }
  } catch (e) { console.warn('[Bonus] getSecretaries failed', e); }
}, { immediate: true });

// ─── 진행률(%) — 전체 문서 union key ────────────────────────────
const activeDocCodes = computed<BonusDocCode[]>(() => BONUS_DOC_CODES);
const overallProgress = computed(() => {
  const keys = new Set<string>();
  for (const c of activeDocCodes.value) BONUS_DOCS[c].keys.forEach((k) => { if (!isOptional(k)) keys.add(k); });
  const list = [...keys];
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

// ─── Create / Update Task ──────────────────────────────────────
const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) { try { const cfg = await getTaskConfigByCode(code); taskConfigSteps.value = (cfg as any)?.processSteps || []; } catch { /* noop */ } }
});

// 각 문서의 [[Signers]] 서명영역 → BonusSigners var-key 와 일치. '' = 서명영역 없음(표/직접 서명).
const SIGNER_KEY_BY_DOC: Record<BonusDocCode, string> = {
  BON01: 'authority_dcr',  // Authority + Capitalisation (DCR board)
  BON02: 'application',     // Letter of Application
  BON03: 'letter',         // Letter of Allotment
  BON04: 'increase',       // Increase of Paid-Up Capital (board)
  BON05: 's76_director',   // Section 76 — Director
  BON06: '',               // Section 76 — Secretary (표 서명)
  BON07: 'statement',      // Statement (이사 전원)
  BON08: 's78_return',     // Section 78 Return (이사 1인)
};

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

// docCode(BON01~08)가 실제 연결 템플릿에 없을 수 있어(파일명만 연결) → 템플릿명 키워드로도 매칭.
const NAME_KEYWORDS: Record<BonusDocCode, RegExp> = {
  BON01: /authority\s*to\s*issue|capitalisation|capitalization|dcr\s*\+?\s*mwr/i,
  BON02: /share\s*application|application\s*letter|letter\s*of\s*application/i,
  BON03: /letter\s*of\s*allotment/i,
  BON04: /increase.*(paid|capital)|paid[-\s]*up\s*capital/i,
  BON05: /section\s*76.*director/i,
  BON06: /section\s*76.*secretary/i,
  BON07: /statement.*(particulars|shares\s*allotted)|statement\s*containing/i,
  BON08: /section\s*78|return\s*of\s*allotment/i,
};
function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
/** 템플릿 원본 파일명(확장자 제거) — prepare-documents 의 label/fileName 로 사용. */
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}
function resolveTemplate(docCode: BonusDocCode): { tmpId: number; label: string } | null {
  const docs = availableTemplates.value as any[];
  const byCode = docs.find((d) => String(d.docCode || '').toUpperCase() === docCode);
  if (byCode?.tmpId) {
    return { tmpId: byCode.tmpId, label: templateFileLabel(byCode, BONUS_DOCS[docCode].label) };
  }
  const re = NAME_KEYWORDS[docCode];
  const byName = docs.find((d) => re.test(tmplName(d)));
  if (byName?.tmpId) {
    return { tmpId: byName.tmpId, label: templateFileLabel(byName, BONUS_DOCS[docCode].label) };
  }
  return null;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  // 미완성이어도 완료 허용: 필수값(company_name 등) 미입력이나 문서 미완성이어도 task 생성을 막지 않는다.
  // 템플릿이 연결된 문서만 생성 대상으로 삼고, 연결 안 된 문서는 조용히 건너뛴다.
  const targetCodes = activeDocCodes.value;
  const resolved = targetCodes
    .map((c) => {
      const hit = resolveTemplate(c);
      return hit ? { c, tmpId: hit.tmpId, label: hit.label } : null;
    })
    .filter((r): r is { c: BonusDocCode; tmpId: number; label: string } => !!r);

  // 연결된 docx 템플릿이 하나도 없으면(=BON01~BON08 미연결) 문서가 0건으로 생성됨 → 조용히 빈 task 만들지 않고 안내.
  if (resolved.length === 0) {
    const avail = (availableTemplates.value as any[]).map((x) => x.templateName || x.docCode).join(', ') || '(none)';
    progress.value = `⚠ 이 업무에 연결된 문서 템플릿이 없습니다. 관리자에서 Bonus Issue 문서(BON01~BON08) 템플릿을 연결해 주세요. 연결됨: [${avail}]`;
    console.warn('[Bonus] no templates linked. selection.documents =', props.selection?.documents);
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      // Register 단계(TaskListPage.isMyAllotment / M32203) → addShareholderForAllotmentTask
      //   → POST entity_my/shares → L_SHAREHOLDER_DETAIL (EVENT_NO 자동 채번)
      shareholders: buildShareholdersForRegister(),
      transactionDate: state.values.allotment_date || '',
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      sharePrice: state.values.share_price || '',
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }
    // 연결된 템플릿이 있는 문서만 준비. 하나도 없으면 문서 생성은 건너뛰고 task 만 완료.
    if (resolved.length) {
      progress.value = 'Preparing documents…';
      const formData = buildBonusVariables(state as BonusState);
      const docsToSave = resolved.map(({ c, tmpId, label }) => {
        const varKey = SIGNER_KEY_BY_DOC[c];
        const signers = varKey ? (state.signers[varKey] || []) : [];
        return {
          templateId: tmpId,
          // prepare-documents 는 label → FILE_NAME. 연결된 Bonus 템플릿 원본명 사용.
          label,
          personName: String(state.values.company_name || 'Company'),
          entityCd: entityCd.value || undefined,
          formData,
          signers: signers.length ? signers : undefined,
        };
      });
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) {
        console.warn('[Bonus] document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[Bonus] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit gather/restore ───────────────────────────────────────
function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    allottees: buildAllottees(state as BonusState),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    sharePrice: state.values.share_price || '',
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    Object.assign(state, { ...emptyBonusState(), ...src });
    state.values = { ...(src.values || {}) };
    state.flags = { ...emptyBonusState().flags, ...(src.flags || {}) };
    state.choices = { ...emptyBonusState().choices, ...(src.choices || {}) };
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Allotment of Shares (Bonus Issue)</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. Bonus Issue — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <BonusIssuePackageDoc
        :studio="!!studio"
        :edit-mode="editMode"
        :creating="creating"
        @create-task="handleCreateTask"
      />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn v-if="!studio" color="primary" class="text-none" :loading="creating" :disabled="creating" :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'" @click="handleCreateTask">{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; align-items:center; gap:8px; }
.co-doc-body { padding:20px; background:#eceff5; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>
